import { Country } from 'country-list';
import { intersection } from 'lodash';
import { IExtendedLibraryRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/critical-to-quality-factors.interface';
import { mergeArrays } from '@utils-clean/array-helpers';
import { RiskStatusType } from 'types/graphql-global-types';
import { notEmpty } from '@utils/utility-helpers';
import { getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem } from '@queries/generated/common.generated';
import { CriticalVariableFields } from '@queries/generated/fragments.generated';
import {
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    riskObservationsRisks_OpraQueries_risks_Risk,
} from '@queries/generated/legacy-risk-assessment.generated';

export const mapNamedItems = (
    items: (getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem | null)[],
    selectedItemNames?: string[] | null,
) =>
    items
        .filter((i) => i !== null)
        .map((item) => ({
            id: item.id,
            name: item.name ?? '',
            groupedBy: item.active ? 'Active' : 'Inactive',
            disabled: selectedItemNames
                ? selectedItemNames.some((name) => name.toLowerCase() === item?.name?.toLowerCase())
                    ? false
                    : !item.active
                : !item.active,
            active: item.active,
            __typename: item.__typename,
        }))
        .sort((a, b) => {
            if (a?.active !== b?.active) {
                return a?.active ? -1 : 1;
            }

            if (a.name && b.name) {
                return a.name?.localeCompare(b.name);
            }

            return a?.active ? -1 : 1;
        });

export const mapCountries = (countries: Country[], selectedCountries: Country[]): Country[] => {
    const sanitisedSelectedCountries = selectedCountries.filter(notEmpty).map(({ code, name }) => ({ code, name }));
    return mergeArrays(countries.filter(notEmpty), sanitisedSelectedCountries, (country: Country) => country.code);
};

export function getAssignableRisks({
    data,
    libraryRisks,
    riskLibraryCategories,
    ctqFactor,
}: {
    data?: { risks: riskObservationsRisks_OpraQueries_risks_Risk[] };
    libraryRisks?: { libraryRisks: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk[] };
    riskLibraryCategories?: {
        study?: {
            riskLibraryCategoryIds?: string[];
        };
    };
    ctqFactor?: CriticalVariableFields;
}): IExtendedLibraryRiskFields[] {
    // Find the library risks that can be assigned to the CtQ Factor.
    let filteredLibraryRisks =
        data?.risks && libraryRisks
            ? (libraryRisks.libraryRisks
                  .filter(
                      (libraryRisk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) =>
                          // Exclude any library risk already added to the study.
                          !data.risks?.find(
                              (studyRisk: riskObservationsRisks_OpraQueries_risks_Risk) =>
                                  studyRisk.libraryReferenceId === libraryRisk.id,
                          ) &&
                          // Exclude any library risk that is not active.
                          libraryRisk.active &&
                          // Exclude any library risk whose category is not in the current study's allowed risk library
                          // categories.
                          ((Array.isArray(libraryRisk.riskLibraryCategoryIds) &&
                              libraryRisk.riskLibraryCategoryIds.length === 0 &&
                              Array.isArray(riskLibraryCategories?.study?.riskLibraryCategoryIds) &&
                              riskLibraryCategories?.study?.riskLibraryCategoryIds.length === 0) ||
                              intersection(
                                  libraryRisk.riskLibraryCategoryIds,
                                  riskLibraryCategories?.study?.riskLibraryCategoryIds,
                              )?.length),
                  )
                  .map((risk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) => ({
                      ...risk,
                      icon: 'book-open',
                      library: true,
                  })) as unknown as IExtendedLibraryRiskFields[])
            : [];

    // Include any study risk not associated with the current CtQ Factor.
    if (ctqFactor)
        filteredLibraryRisks = filteredLibraryRisks.concat(
            data?.risks
                .filter(
                    (studyRisk: riskObservationsRisks_OpraQueries_risks_Risk) =>
                        !ctqFactor?.associatedRiskIds || ctqFactor?.associatedRiskIds.indexOf(studyRisk.id) < 0,
                )
                .map((risk: riskObservationsRisks_OpraQueries_risks_Risk) => ({
                    ...risk,
                    library: false,
                })) as unknown as IExtendedLibraryRiskFields[],
        );

    return filteredLibraryRisks as IExtendedLibraryRiskFields[];
}

export function getFilteredRisks(risks: IExtendedLibraryRiskFields[]): {
    filteredRisks: IExtendedLibraryRiskFields[];
    allRisksLength: number;
} {
    return {
        filteredRisks: risks.filter((risk) => risk.status !== RiskStatusType.NOT_APPLICABLE),
        allRisksLength: risks.length,
    };
}

export function getFilteredRisksExcludeObsolete(risks: IExtendedLibraryRiskFields[]): {
    filteredRisks: IExtendedLibraryRiskFields[];
    allRisksLength: number;
} {
    return {
        filteredRisks: risks.filter(
            (risk) => risk.status !== RiskStatusType.NOT_APPLICABLE && risk.status !== RiskStatusType.OBSOLETE,
        ),
        allRisksLength: risks.length,
    };
}
